import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { P404ExtComponent } from '@pages/error/p404.ext.component';
import { P500ExtComponent } from '@pages/error/p500.ext.component';
import { ForgotPasswordComponent } from '@pages/forgot-password/forgot-password.component';
import { ResetPasswordComponent } from '@pages/reset-password/reset-password.component';
import { DashboardComponent } from '@pages/dashboard/dashboard.component';
import { SupportComponent } from '@pages/support/support.component';
import { VerifyEmailComponent } from '@pages/verify-email/verify-email.component';
import {
  AdminJobComponent,
  AdminJobDetailComponent, DataConnectionComponent, DataConnectionTemplatesComponent, DeviceComponent,
  EmailComponent,
  JobComponent,
  JobDetailComponent, MdmDetailComponent, ScannerDetailComponent,
  SoftwareArtifactReleaseTypeComponent, SoftwareArtifactSingleComponent,
  SoftwareDetailComponent,
  SoftwareListComponent,
  SoftwareOverviewComponent,
  SoftwarePlatformComponent,
  SoftwareTypeComponent,
  WorkflowComponent,
  WorkflowDetailComponent,
  WorkflowExecutionComponent,
  WorkflowExecutionDetailComponent,
  WorkflowTemplateComponent,
} from '@nimmsta/ng-workflow-lib';
import {
  AuthGuard, ProfileComponent, ProfileSessionComponent, WaitingListComponent,
} from '@nimmsta/ng-commons-lib';
import {
  AllUserComponent,
  OrganizationsComponent,
  OrganizationUserComponent, UserOverviewComponent,
  ConfigurationCustomerWrapperComponent,
  OrganizationSettingsComponent,
} from '@nimmsta/ng-b2b-lib';
import { LicensesComponent } from '@pages/licenses/licenses.component';
import { InformationComponent } from '@pages/information/information.component';
import { InformationItemComponent } from '@pages/information/information-item.component';
import { StartPageComponent } from '@pages/start-page/start-page.component';
import { RequestAccessComponent } from '@pages/request-access/request-access.component';
import { EditDashboardComponent } from '@pages/dashboard/edit/edit-dashboard.component';
import { LayoutComponent } from './layout/layout.component';

const routes: Routes = [
  {
    path: '',
    redirectTo: 'start',
    pathMatch: 'full',
  },
  {
    path: '500',
    component: P500ExtComponent,
  },
  {
    path: 'verify-email',
    component: VerifyEmailComponent,
  },
  {
    path: 'forgot-password',
    component: ForgotPasswordComponent,
  },
  {
    path: 'reset-password',
    component: ResetPasswordComponent,
  },
  {
    path: 'register',
    redirectTo: 'request-access',
  },
  {
    path: 'request-access',
    component: RequestAccessComponent,
  },
  {
    path: 'waiting-list',
    component: WaitingListComponent,
  },
  {
    path: '',
    component: LayoutComponent,
    canActivate: [AuthGuard],
    children: [
      {
        path: '',
        component: DashboardComponent,
        canActivate: [AuthGuard],
      },
      { path: 'start', component: StartPageComponent },
      {
        path: 'dashboard',
        component: DashboardComponent,
        canActivate: [AuthGuard],
      },
      {
        path: 'dashboard-edit',
        component: EditDashboardComponent,
        canActivate: [AuthGuard],
      },
      {
        path: 'workflows',
        component: WorkflowComponent,
        canActivate: [AuthGuard],
      },
      {
        path: 'workflow-templates',
        component: WorkflowTemplateComponent,
        canActivate: [AuthGuard, AuthGuard.isAdmin],
      },
      {
        path: 'workflows/:id',
        component: WorkflowDetailComponent,
        canActivate: [AuthGuard],
      },
      {
        path: 'workflows/:workflow_id/executions',
        component: WorkflowExecutionComponent,
        canActivate: [AuthGuard],
      },
      {
        path: 'workflows/:workflow_id/executions/:id',
        component: WorkflowExecutionDetailComponent,
        canActivate: [AuthGuard],
      },
      {
        path: 'workflow-emails',
        component: EmailComponent,
        canActivate: [AuthGuard],
      },
      {
        path: 'users',
        component: UserOverviewComponent,
        canActivate: [AuthGuard],
      },
      {
        path: 'users-all',
        component: AllUserComponent,
        canActivate: [AuthGuard, AuthGuard.isAdmin],
      },
      {
        path: 'dataconnection',
        component: DataConnectionComponent,
        canActivate: [AuthGuard],
      },
      {
        path: 'dataconnection-templates',
        component: DataConnectionTemplatesComponent,
        canActivate: [AuthGuard, AuthGuard.isAdmin],
      },
      {
        path: 'jobs',
        component: JobComponent,
        canActivate: [AuthGuard],
      },
      {
        path: 'jobs/:id',
        component: JobDetailComponent,
        canActivate: [AuthGuard],
      },
      {
        path: 'organizations',
        component: OrganizationsComponent,
        canActivate: [AuthGuard, AuthGuard.isAdmin],
      },
      {
        path: 'organizations/:id',
        component: OrganizationUserComponent,
        canActivate: [AuthGuard, AuthGuard.isAdmin],
      },
      {
        path: 'organizations/:id/settings',
        component: OrganizationSettingsComponent,
        canActivate: [AuthGuard],
      },
      {
        path: 'admin-jobs',
        component: AdminJobComponent,
        canActivate: [AuthGuard, AuthGuard.isAdmin],
      },
      {
        path: 'admin-jobs/:id',
        component: AdminJobDetailComponent,
        canActivate: [AuthGuard, AuthGuard.isAdmin],
      },
      {
        path: 'software',
        component: SoftwareOverviewComponent,
        canActivate: [AuthGuard],
      },
      {
        path: 'software/types',
        component: SoftwareTypeComponent,
        canActivate: [AuthGuard],
      },
      {
        path: 'software/platforms',
        component: SoftwarePlatformComponent,
        canActivate: [AuthGuard],
      },
      {
        path: 'software/artifactReleaseTypes',
        component: SoftwareArtifactReleaseTypeComponent,
        canActivate: [AuthGuard],
      },
      {
        path: 'software/:softwareId',
        component: SoftwareDetailComponent,
        canActivate: [AuthGuard],
      },
      {
        path: 'software/type/:typeId',
        component: SoftwareListComponent,
        canActivate: [AuthGuard],
      },
      {
        path: 'software/:softwareId/:artifactId',
        component: SoftwareArtifactSingleComponent,
        canActivate: [AuthGuard],
      },
      {
        path: 'configurations',
        component: ConfigurationCustomerWrapperComponent,
        canActivate: [AuthGuard],
      },
      {
        path: 'profile',
        component: ProfileComponent,
        canActivate: [AuthGuard],
      },
      {
        path: 'profile/sessions',
        component: ProfileSessionComponent,
        canActivate: [AuthGuard],
      },
      {
        path: 'support',
        component: SupportComponent,
        canActivate: [AuthGuard],
      },
      {
        path: 'devices',
        component: DeviceComponent,
        canActivate: [AuthGuard],
      },
      {
        path: 'devices/mdm/:id',
        component: MdmDetailComponent,
        canActivate: [AuthGuard],
      },
      {
        path: 'devices/smartwatches/:id',
        component: ScannerDetailComponent,
        canActivate: [AuthGuard],
      },
      {
        path: 'information',
        component: InformationComponent,
      },
      {
        path: 'information/:id',
        component: InformationItemComponent,
      },
      {
        path: 'licenses',
        component: LicensesComponent,
      },
    ],
  },
  {
    path: '**',
    component: P404ExtComponent,
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule {}
